/**
 * LoggedItemsToggle — toggle button for showing/hiding completed/canceled
 * items in container views (Project, List, Area).
 *
 * v1 parity: "Show X logged items" / "Hide X logged items" with eye icon,
 * marginTop: 40px, primary color, font-weight 600.
 *
 * Per master plan §11: placed below the "Later Items" section.
 */

import { memo } from "react"
import { useTranslation } from "react-i18next"
import EyeIcon from "@/shared/icons/totodoicons/stroke/eye.svg?react"
import EyeOffIcon from "@/shared/icons/pikaicons/stroke/security/eye-off.svg?react"

interface LoggedItemsToggleProps {
  /** Number of logged (completed + canceled) items */
  count: number
  /** Whether the logged section is currently expanded */
  showLogged: boolean
  /** Toggle visibility */
  onToggle: (show: boolean) => void
}

export const LoggedItemsToggle = memo(function LoggedItemsToggle({
  count,
  showLogged,
  onToggle,
}: LoggedItemsToggleProps) {
  const { t } = useTranslation()

  if (count === 0) return null

  const Icon = showLogged ? EyeOffIcon : EyeIcon
  const label = showLogged
    ? t("Hide {{count}} logged", { count })
    : t("Show {{count}} logged", { count })

  return (
    <div className="mt-10">
      <button
        type="button"
        onClick={() => onToggle(!showLogged)}
        className="flex cursor-pointer items-center gap-1.5 px-2.5 text-primary"
      >
        <Icon className="size-[18px]" />
        <span className="text-[15px] font-semibold">{label}</span>
      </button>
    </div>
  )
})
